import React from 'react';
import SafeIcon from '../common/SafeIcon';
import { 
  FiTarget, FiTrendingUp, FiUsers, FiAward, FiCpu, FiShield, 
  FiMessageSquare, FiSearch, FiZap, FiBookOpen, FiArrowRight,
  FiLayout, FiActivity, FiGlobe, FiPieChart
} from 'react-icons/fi';
import { motion } from 'framer-motion';

const MarketingStudyGuide = ({ onBack }) => {
  const sections = [
    {
      title: "Core Brand Identity",
      icon: FiShield,
      color: "text-blue-600",
      bg: "bg-blue-50",
      content: "NetDesk Pro is the 'Flight Simulator' for IT Support. We bridge the gap between theoretical knowledge (CompTIA A+) and real-world execution. Our brand voice is authoritative, high-tech, and results-oriented."
    },
    {
      title: "The Problem We Solve",
      icon: FiActivity,
      color: "text-red-500",
      bg: "bg-red-50",
      content: "The 'Experience Gap.' Entry-level IT hires often have certifications but freeze when faced with a live user. We eliminate the risk of 'on-the-job' learning at the expense of client satisfaction."
    }
  ];

  const personas = [
    {
      name: "The Career Reloader",
      desc: "Individuals switching from retail/hospitality to tech. They need confidence and a portfolio of 'simulated experience' to land their first $50k role.",
      pain: "Fear of sounding like they don't know what they're doing on a call."
    },
    {
      name: "The MSP Owner",
      desc: "Small to mid-sized Managed Service Providers who are losing money because their L1 techs take 2 hours to solve a 15-minute ticket.",
      pain: "High churn rate and expensive training overhead."
    },
    {
      name: "The IT Student",
      desc: "Enrolled in college or bootcamps. They have the theory but are terrified of the 'real' command line and enterprise AD environments.",
      pain: "Resume looks empty despite having multiple certifications."
    }
  ];

  return (
    <div className="min-h-screen bg-white text-slate-900 pb-20">
      {/* Header - Increased padding top and bottom for more breathing room */}
      <header className="bg-slate-900 pt-24 pb-32 px-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/3 h-full bg-blue-600/10 blur-[100px] rounded-full"></div>
        <div className="max-w-5xl mx-auto relative z-10">
          <div className="inline-flex items-center space-x-2 bg-blue-500/20 text-blue-400 px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest mb-6">
            <SafeIcon icon={FiPieChart} />
            <span>Strategic Internal Resource</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-black text-white mb-4">Marketing Study <span className="text-blue-500">Guide</span></h1>
          <p className="text-slate-400 text-lg max-w-2xl font-medium">Understanding the psychology, market positioning, and growth levers of the NetDesk Pro ecosystem.</p>
        </div>
      </header>

      {/* Main Content - Increased negative margin to pull cards up over the larger header space, added relative z-20 */}
      <div className="max-w-5xl mx-auto px-6 -mt-20 relative z-20">
        {/* Executive Summary Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {sections.map((s, i) => (
            <motion.div 
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-xl"
            >
              <div className={`w-12 h-12 ${s.bg} ${s.color} rounded-2xl flex items-center justify-center mb-6`}>
                <SafeIcon icon={s.icon} className="text-2xl" />
              </div>
              <h3 className="text-xl font-black text-slate-900 mb-4">{s.title}</h3>
              <p className="text-slate-500 text-sm leading-relaxed font-medium">{s.content}</p>
            </motion.div>
          ))}
        </div>

        {/* Value Pillars */}
        <section className="mb-16">
          <div className="flex items-center space-x-3 mb-8">
            <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center text-slate-900 font-black">1</div>
            <h2 className="text-2xl font-black text-slate-900 uppercase tracking-tight">Core Value Pillars</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="space-y-4">
              <h4 className="text-blue-600 font-black text-xs uppercase tracking-widest">Pillar 01</h4>
              <h3 className="text-lg font-black text-slate-900">Psychological Safety</h3>
              <p className="text-sm text-slate-500 leading-relaxed">We provide a 'Safe-to-Fail' environment. Marketing should emphasize that analysts can 'crash the server' here so they don't do it at work.</p>
            </div>
            <div className="space-y-4">
              <h4 className="text-blue-600 font-black text-xs uppercase tracking-widest">Pillar 02</h4>
              <h3 className="text-lg font-black text-slate-900">Proof of Capability</h3>
              <p className="text-sm text-slate-500 leading-relaxed">Unlike MCQ-based exams, our certifications prove the analyst can actually click the right buttons in a high-pressure scenario.</p>
            </div>
            <div className="space-y-4">
              <h4 className="text-blue-600 font-black text-xs uppercase tracking-widest">Pillar 03</h4>
              <h3 className="text-lg font-black text-slate-900">The SOP Habit</h3>
              <p className="text-sm text-slate-500 leading-relaxed">We don't just teach tech; we teach documentation dependency. We market to managers who want disciplined, process-driven employees.</p>
            </div>
          </div>
        </section>

        {/* User Personas */}
        <section className="mb-16 bg-slate-50 rounded-[3rem] p-10 md:p-16 border border-slate-100">
          <div className="max-w-2xl mb-12">
            <h2 className="text-3xl font-black text-slate-900 mb-4">Market Segmentation</h2>
            <p className="text-slate-500 font-medium">Who are we talking to? Focus marketing spend on these three high-converting profiles.</p>
          </div>
          <div className="space-y-8">
            {personas.map((p, i) => (
              <div key={i} className="flex flex-col md:flex-row gap-6 items-start bg-white p-8 rounded-3xl border border-slate-200 shadow-sm">
                <div className="w-12 h-12 bg-blue-600 text-white rounded-2xl flex items-center justify-center shrink-0 shadow-lg shadow-blue-200">
                  <SafeIcon icon={FiTarget} />
                </div>
                <div>
                  <h3 className="text-xl font-black text-slate-900 mb-2">{p.name}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-4">{p.desc}</p>
                  <div className="inline-flex items-center space-x-2 bg-red-50 text-red-600 px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest">
                    <span>Critical Pain Point: {p.pain}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Marketing Funnel */}
        <section className="mb-16">
          <h2 className="text-2xl font-black text-slate-900 mb-8 uppercase tracking-tight">The Growth Funnel</h2>
          <div className="border border-slate-200 rounded-[2.5rem] overflow-hidden">
            <div className="bg-slate-900 text-white p-6 border-b border-slate-800">
              <div className="flex justify-between items-center">
                <span className="text-[10px] font-black uppercase tracking-[0.3em] text-blue-400">Phase</span>
                <span className="text-[10px] font-black uppercase tracking-[0.3em] text-blue-400">Activity</span>
              </div>
            </div>
            <div className="divide-y divide-slate-100">
              <div className="p-8 flex flex-col md:flex-row md:items-center gap-6">
                <div className="w-32 shrink-0 text-sm font-black text-slate-900 uppercase">01. Awareness</div>
                <div className="flex-1 text-sm text-slate-600">LinkedIn ads targeting 'IT Support' job seekers with clips of the 'CrashLoopBackOff' simulation.</div>
              </div>
              <div className="p-8 flex flex-col md:flex-row md:items-center gap-6">
                <div className="w-32 shrink-0 text-sm font-black text-slate-900 uppercase">02. Interest</div>
                <div className="flex-1 text-sm text-slate-600">Downloadable 'NetDesk Pro Playbook' (eBook) in exchange for email. Provides value immediately.</div>
              </div>
              <div className="p-8 flex flex-col md:flex-row md:items-center gap-6">
                <div className="w-32 shrink-0 text-sm font-black text-slate-900 uppercase">03. Desire</div>
                <div className="flex-1 text-sm text-slate-600">Free 'Guest Session' simulation. Let them feel the rush of resolving a ticket before asking for money.</div>
              </div>
              <div className="p-8 flex flex-col md:flex-row md:items-center gap-6">
                <div className="w-32 shrink-0 text-sm font-black text-slate-900 uppercase">04. Action</div>
                <div className="flex-1 text-sm text-slate-600">Enrollment in Tier 1 Certification Track. Low-friction entry point to the full ecosystem.</div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <div className="bg-blue-600 rounded-[3rem] p-12 text-center text-white shadow-2xl shadow-blue-200">
          <h2 className="text-3xl font-black mb-4">Ready to launch this campaign?</h2>
          <p className="text-blue-100 mb-8 max-w-xl mx-auto">This study guide serves as the foundation for all creative assets, ad copy, and sales outreach.</p>
          <button onClick={onBack} className="bg-white text-blue-600 px-10 py-4 rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-blue-50 transition-all flex items-center justify-center mx-auto space-x-2">
            <span>Return to Terminal</span>
            <SafeIcon icon={FiArrowRight} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default MarketingStudyGuide;